/**
 * @fileoverview Text Masking Utilities
 *
 * This module provides utilities for masking sensitive PII (Personally Identifiable Information)
 * in strings and data objects. It's commonly used for:
 * - Logging sensitive data safely
 * - Displaying partially hidden user information in UI
 * - Protecting user privacy in analytics and error reporting
 */

/**
 * Defines the masking behavior for sensitive data.
 *
 * @enum {number}
 * @example
 * // No masking - returns original value
 * applyMask("john@email.com", MaskMode.None); // "john@email.com"
 *
 * // Full masking - replaces all characters with asterisks
 * applyMask("john@email.com", MaskMode.Full); // "**************"
 *
 * // Partial masking - keeps first char, masks next 4, keeps rest
 * applyMask("john@email.com", MaskMode.Partial); // "j****email.com"
 */
export enum MaskMode {
    /** No masking applied - returns the original value unchanged */
    None = 0,
    /** Full masking - replaces entire string with asterisks (*) */
    Full = 1,
    /** Partial masking - keeps first character, masks 4 characters, keeps the rest */
    Partial = 2,
}

/**
 * List of field names that contain sensitive PII data and should be masked.
 * Used by `applyMaskToData` to automatically identify and mask sensitive fields.
 *
 * @constant
 * @readonly
 */
const fieldsToMaskDirect = [
    'userName',
    'username',
    'foreignAccountId',
    'msisdn',
    'email',
    '$email',
    '$name',
    '$user_id',
    'phone',
    'Phone',
    'Email',
    'Email Address',
    'Mobile',
    'Mobile number',
    'User Name',
    'User ID',
    'userId',
    'txRef',
    'URL',
] as const;

/**
 * Applies masking to a single string value based on the specified mode.
 *
 * Masking behavior:
 * - `MaskMode.None`: Returns the original value unchanged
 * - `MaskMode.Full`: Replaces all characters with asterisks (*)
 * - `MaskMode.Partial`: Keeps first character, replaces next 4 with "****", keeps the rest
 *   - For strings shorter than 6 characters, returns "****" for privacy
 *
 * @param {string} value - The string value to mask
 * @param {MaskMode} mode - The masking mode to apply
 * @returns {string} The masked string, or empty string if input is invalid
 *
 * @example
 * // Partial masking (most common use case)
 * applyMask("johndoe@email.com", MaskMode.Partial); // "j****oe@email.com"
 * applyMask("+971501234567", MaskMode.Partial);     // "+****01234567"
 *
 * @example
 * // Full masking
 * applyMask("secret123", MaskMode.Full); // "*********"
 *
 * @example
 * // Short strings get fixed mask for privacy
 * applyMask("abc", MaskMode.Partial); // "****"
 */
export function applyMask(value: string, mode: MaskMode): string {
    // Guard against null/undefined/empty values
    if (!value || typeof value !== 'string') {
        console.warn('[applyMask] - Value is null, undefined, empty, or not a string');
        return value ?? '';
    }

    // Guard against values that are already masked
    if (value.includes('*')) {
        return value;
    }

    if (mode === MaskMode.None) return value;
    if (mode === MaskMode.Full) return '*'.repeat(value.length);
    if (mode === MaskMode.Partial) {
        // Short strings return fixed mask to prevent exposing too much info
        if (value.length < 6) return '****';
        const firstCharacter = value.substring(0, 1);
        const remainingCharacters = value.substring(5);
        return firstCharacter + '****' + remainingCharacters;
    }

    // Default return original value if mode is unrecognized
    return value;
}

/**
 * Applies masking to all sensitive fields in a data object.
 *
 * Automatically identifies and masks fields that contain PII data based on
 * the predefined `fieldsToMaskDirect` list. This is useful for sanitizing
 * objects before logging or sending to analytics services.
 *
 * @param {Record<string, unknown> | null | undefined} data - The data object to mask
 * @param {MaskMode} mode - The masking mode to apply to sensitive fields
 * @param {boolean} [mutateOriginal=false] - If true, modifies the original object in place.
 *        If false (default), creates a shallow copy before masking to preserve the original.
 * @returns {Record<string, unknown>} A new object (or the original if mutateOriginal=true) with sensitive fields masked
 *
 * @example
 * const userData = {
 *   userName: "johndoe",
 *   email: "john@example.com",
 *   action: "login",
 *   timestamp: 1234567890
 * };
 *
 * // Default behavior - creates a copy, original unchanged
 * const masked = applyMaskToData(userData, MaskMode.Partial);
 * // Result:
 * // {
 * //   userName: "j****oe",
 * //   email: "j****example.com",
 * //   action: "login",           // Not in sensitive fields list - unchanged
 * //   timestamp: 1234567890      // Not a string - unchanged
 * // }
 * // userData is still intact with original values
 *
 * @example
 * // Mutate original object for performance (avoids copy)
 * applyMaskToData(userData, MaskMode.Partial, true);
 * // userData is now modified in place
 *
 * @example
 * // Safe to call with null/undefined - returns empty object
 * applyMaskToData(null, MaskMode.Partial); // {}
 */

export function applyMaskToData(
    data: Record<string, unknown> | null | undefined,
    mode: MaskMode,
    mutateOriginal = false
): Record<string, unknown> {
    // Guard against null/undefined data
    if (!data || typeof data !== 'object') {
        console.warn('[applyMaskToData] - Data parameter is null, undefined, or not an object');
        return data ?? {};
    }

    try {
        // Create a shallow copy to avoid mutating the original object
        const jsonObject = mutateOriginal ? data : { ...data };

        // Iterate through known sensitive fields and mask them if present
        for (const fieldName of fieldsToMaskDirect) {
            const sensitiveValue = jsonObject[fieldName];
            if (typeof sensitiveValue === 'string' && sensitiveValue.length > 0) {
                jsonObject[fieldName] = applyMask(sensitiveValue, mode);
            }
        }

        return jsonObject;
    } catch (error) {
        console.error('[applyMaskToData] - Failed to mask the data:', error);
        return data; // Return original if parsing fails
    }
}

/**
 * Masks email addresses and phone/MSISDN numbers embedded inside a plain string.
 *
 * Replaces:
 * - Email addresses: keeps first char + domain TLD, masks the middle
 * - Phone/MSISDN numbers (7+ consecutive digits): keeps first digit, masks next 4, keeps rest
 *
 * @param {string} text - The raw string that may contain PII
 * @returns {string} The string with PII patterns replaced by masked equivalents
 */
export function maskSensitiveInText(text: string): string {
    if (!text || typeof text !== 'string') return text ?? '';

    // Mask email addresses: keep first char only, mask rest of local part, keep @domain
    const maskedEmail = text.replace(
        /([a-zA-Z0-9._%+-])([a-zA-Z0-9._%+-]*)(@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
        (_, first, _middle, domain) => `${first}****${domain}`
    );

    // Mask phone/MSISDN numbers (7+ consecutive digits): keep first digit, mask next 4, keep rest
    return maskedEmail.replace(/\b(\d)(\d{4})(\d{2,})\b/g, (_, first, _middle, rest) => `${first}****${rest}`);
}

/**
 * Sanitizes error data by masking embedded PII (email addresses and phone/MSISDN numbers).
 *
 * Accepts either:
 * - A **string** — e.g. `error.data.msg` — and returns a masked string directly.
 * - An **object** — e.g. the full `e.data` payload — and returns a new object with all
 *   string fields sanitized.
 *
 * Unlike `applyMaskToData` which masks known field keys, this function scans
 * every string value for inline PII patterns — useful for API error responses
 * where MSISDN/email appear inside `description` or `message` fields.
 *
 * @example
 * // String input
 * maskErrorData("Msisdn: 966541123451 is in use.");
 * // "Msisdn: 9****123451 is in use."
 *
 * // Object input
 * maskErrorData({ description: "Msisdn: 966541123451 is in use.", status: 400 });
 * // { description: "Msisdn: 9****123451 is in use.", status: 400 }
 */
export function maskErrorData(data: string): string;
export function maskErrorData(data: Record<string, unknown> | null | undefined): Record<string, unknown>;
export function maskErrorData(
    data: string | Record<string, unknown> | null | undefined
): string | Record<string, unknown> {
    // String path — mask inline PII directly
    if (typeof data === 'string') {
        return maskSensitiveInText(data);
    }

    // Object path — sanitize all string fields
    if (!data || typeof data !== 'object') {
        return data ?? {};
    }

    try {
        const sanitized: Record<string, unknown> = { ...data };
        for (const key of Object.keys(sanitized)) {
            if (typeof sanitized[key] === 'string') {
                sanitized[key] = maskSensitiveInText(sanitized[key] as string);
            }
        }
        return sanitized;
    } catch (error) {
        console.error('[maskErrorData] - Failed to mask error data:', error);
        return data;
    }
}
